<template>
  <v-dialog
    :value="value"
    max-width="760"
    persistent
    scrollable
    :fullscreen="$vuetify.breakpoint.xsOnly"
    @input="$emit('input', $event)"
  >
    <v-card rounded="lg" elevation="3" class="register-dialog">
      <v-card-title class="dialog-header">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-keyboard-return</v-icon>
          <span class="text-h6 font-weight-medium">
            {{ stage === 'put_away' ? 'Put away' : 'Register return' }}
            <span v-if="detail" class="grey--text text--darken-1 ml-1">{{ context.parcel_name || detail.order.name }}</span>
          </span>
        </div>
        <v-spacer></v-spacer>
        <v-btn icon @click="close" :disabled="saving">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="dialog-content">
        <!-- Loading -->
        <div v-if="loading" class="loading-container">
          <v-progress-circular indeterminate color="primary" size="48" />
          <div class="mt-4 grey--text">Fetching order from Shopify...</div>
        </div>

        <!-- Stage 1: what came back and in which condition -->
        <template v-else-if="stage === 'register' && detail">
          <div class="order-summary mb-4">
            <div>
              <div class="text-subtitle-1 font-weight-medium">{{ detail.order.customer_name || detail.order.email || 'Customer' }}</div>
              <div class="text-caption grey--text">
                Ordered {{ detail.order.created_at | DateFilter }}
                <span v-if="context.tracking_number"> · Tracking {{ context.tracking_number }}</span>
              </div>
            </div>
            <v-chip small label :color="expected_total ? 'primary' : 'orange'" text-color="white">
              <v-icon left small>{{ expected_total ? 'mdi-clipboard-check-outline' : 'mdi-package-variant' }}</v-icon>
              {{ expected_total ? `Return ${open_return_names} open in Shopify` : 'No return open in Shopify yet' }}
            </v-chip>
          </div>

          <v-alert v-if="!rows.length" type="info" dense text>
            Nothing on this order can be returned: no shipped items, or everything is already returned or refunded.
          </v-alert>

          <div v-if="has_parcel_scope && parcel_rows.length && other_rows.length" class="group-label">
            <v-icon small class="mr-1">mdi-package-variant-closed</v-icon>
            In parcel {{ context.parcel_name || '' }}<span v-if="context.tracking_number" class="grey--text"> · {{ context.tracking_number }}</span>
          </div>
          <v-alert v-else-if="has_parcel_scope && rows.length && !parcel_rows.length" type="warning" dense text>
            None of the items in this parcel can be returned any more. Other items on the order are listed below.
          </v-alert>

          <v-card
            v-for="row in parcel_rows"
            :key="row.fulfillment_line_item_id"
            outlined
            rounded="lg"
            class="item-row mb-3"
            :class="{ 'item-row--inactive': !row.receive_quantity, 'item-row--disabled': !row.max_quantity }"
          >
            <ReturnItemRow :row="row" :allow_hold="allow_hold" :photos_enabled="photos_enabled" :photos_required="photos_required" @step="step" @pick_photo="pick_photo" />
          </v-card>

          <template v-if="has_parcel_scope && other_rows.length">
            <div class="group-label group-label--other" @click="show_other = !show_other">
              <v-icon small class="mr-1">{{ show_other ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
              Other items on {{ detail.order.name }}, shipped in another parcel ({{ other_rows.length }})
              <span class="grey--text ml-1">· add only if they were in this box</span>
            </div>
            <v-expand-transition>
              <div v-if="show_other">
                <v-card
                  v-for="row in other_rows"
                  :key="row.fulfillment_line_item_id"
                  outlined
                  rounded="lg"
                  class="item-row item-row--other mb-3"
                  :class="{ 'item-row--inactive': !row.receive_quantity, 'item-row--disabled': !row.max_quantity }"
                >
                  <ReturnItemRow :row="row" :allow_hold="allow_hold" :photos_enabled="photos_enabled" :photos_required="photos_required" @step="step" @pick_photo="pick_photo" />
                </v-card>
              </div>
            </v-expand-transition>
          </template>

          <input ref="photoInput" type="file" accept="image/*" capture="environment" class="d-none" @change="on_photo_picked" />
        </template>

        <!-- Stage 2: put goods back -->
        <template v-else-if="stage === 'put_away'">
          <v-alert v-if="!put_away_items.length" type="success" text dense>
            Return registered. Nothing needs to be put on a shelf.
          </v-alert>
          <div v-else class="put-away-progress text-center text-caption grey--text mb-2">
            {{ Math.min(put_away_index + 1, put_away_items.length) }} / {{ put_away_items.length }}
          </div>
          <transition-group name="fade-list" tag="div">
            <ReturnCard
              v-for="(item, i) in put_away_items"
              :key="item.id"
              v-show="i >= put_away_index"
              :item="item"
              :mode="item.mode"
              :active="i === put_away_index"
              @done="put_away_done"
              class="mb-4"
            />
          </transition-group>
        </template>

        <v-alert v-if="error" type="error" dense text class="mt-3">{{ error }}</v-alert>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <template v-if="stage === 'register'">
          <div class="text-caption grey--text">{{ summary }}</div>
          <v-spacer></v-spacer>
          <v-btn text rounded @click="close" :disabled="saving">Cancel</v-btn>
          <v-btn
            color="primary"
            rounded
            elevation="1"
            class="px-6"
            :disabled="!can_submit"
            :loading="saving"
            @click="submit"
          >
            <v-icon left>mdi-check</v-icon>
            {{ expected_total ? 'Confirm receipt' : 'Open return & confirm' }}
          </v-btn>
        </template>
        <template v-else>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            rounded
            elevation="1"
            class="px-6"
            :disabled="put_away_index < put_away_items.length"
            @click="finish"
          >
            <v-icon left>mdi-check-all</v-icon>
            Done
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import ReturnCard from '@/components/ReturnCard'
import ReturnItemRow from '@/components/returns/ReturnItemRow'
import { DateFilter } from '@/utils/filters'

export default {
  components: { ReturnCard, ReturnItemRow },
  filters: { DateFilter },
  props: {
    value: { type: Boolean, default: false },
    // { order_id, source: 'solicited'|'unsolicited', tracking_number, fulfillment_doc_id, preselect: 'expected'|'all' }
    context: { type: Object, default: () => ({}) },
  },
  data(){
    return {
      loading: false,
      saving: false,
      error: '',
      stage: 'register',
      detail: null,
      rows: [],
      result: null,
      put_away_index: 0,
      photo_target: null,
      show_other: false,
    }
  },
  computed: {
    ...mapState({
      company: state => state.warehouse.company,
    }),
    ...mapGetters({
      get_item_position: 'order/get_item_position',
    }),
    allow_hold(){
      return this.company.return_allow_hold !== false
    },
    photos_enabled(){
      return (this.company.return_photo_mode || 'optional') !== 'off'
    },
    photos_required(){
      return this.company.return_photo_mode === 'required'
    },
    expected_total(){
      return this.detail ? this.detail.expected_quantity : 0
    },
    open_return_names(){
      return (this.detail?.returns || []).filter(r => r.status === 'OPEN').map(r => r.name).join(', ')
    },
    selected(){
      return this.rows.filter(r => r.receive_quantity > 0)
    },
    // Opened from a specific parcel: only that fulfillment's items are expected in the box
    has_parcel_scope(){
      return !!this.context.fulfillment_gid
    },
    parcel_rows(){
      return this.rows.filter(r => r.in_parcel)
    },
    other_rows(){
      return this.rows.filter(r => !r.in_parcel)
    },
    missing_photos(){
      return this.photos_required && this.selected.some(r => ['damaged', 'hold'].includes(r.condition) && !r.photos.length)
    },
    can_submit(){
      return !this.saving && this.selected.length > 0 && !this.missing_photos
    },
    summary(){
      if(!this.selected.length) return 'Select what came back'
      const units = this.selected.reduce((tot, r) => tot + r.receive_quantity, 0)
      const parts = ['good', 'damaged', 'hold', 'missing']
        .map(c => ({ c, n: this.selected.filter(r => r.condition === c).reduce((tot, r) => tot + r.receive_quantity, 0) }))
        .filter(p => p.n)
        .map(p => `${p.n} ${p.c}`)
      return `${units} unit${units === 1 ? '' : 's'}: ${parts.join(', ')}${this.missing_photos ? ' · photo required' : ''}`
    },
    // Good items first, sorted by shelve like the pick flow, then damaged and held goods
    put_away_items(){
      if(!this.result) return []
      const items = this.result.items
        .filter(i => i.condition !== 'missing')
        .map(i => ({ ...i, mode: i.condition === 'good' ? 'shelf' : i.condition, sf_position: i.uuid ? this.get_item_position(i.uuid) : null }))
      const order = { shelf: 0, damaged: 1, hold: 2 }
      return items.sort((a, b) => order[a.mode] - order[b.mode]
        || (!a.sf_position ? 1 : !b.sf_position ? -1 : a.sf_position.shelve_index - b.sf_position.shelve_index))
    },
  },
  watch: {
    value(open){
      if(open) this.load()
    },
  },
  methods: {
    reset(){
      this.stage = 'register'
      this.detail = null
      this.rows = []
      this.result = null
      this.error = ''
      this.put_away_index = 0
    },
    async load(){
      this.reset()
      if(!this.context.order_id) return
      this.loading = true
      try {
        const [detail] = await this.$store.dispatch('returns/find_order', { order_id: this.context.order_id })
        this.detail = detail
        const fulfillment_gid = this.context.fulfillment_gid || null
        const tracking = (this.context.tracking_number || '').toUpperCase()
        this.rows = detail.items
          .filter(item => item.quantity > 0)
          .map(item => {
            const in_parcel = fulfillment_gid
              ? item.fulfillment_id === fulfillment_gid || (!!tracking && item.tracking_numbers.some(t => String(t).toUpperCase() === tracking))
              : true
            const max_quantity = item.expected_quantity + item.returnable_quantity
            // Expected units are always preselected; "all" only applies to the parcel that was scanned
            const preselect = item.expected_quantity
              || (this.context.preselect === 'all' && in_parcel ? item.returnable_quantity : 0)
            return { ...item, in_parcel, max_quantity, receive_quantity: Math.min(preselect, max_quantity), condition: 'good', note: '', photos: [] }
          })
          .sort((a, b) => b.max_quantity - a.max_quantity)
        // Expand the other items straight away when nothing from the scanned parcel is returnable
        this.show_other = this.has_parcel_scope && !this.rows.some(r => r.in_parcel)
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },
    step(row, delta){
      row.receive_quantity = Math.max(0, Math.min(row.max_quantity, row.receive_quantity + delta))
    },
    pick_photo(row){
      this.photo_target = row
      this.$refs.photoInput.value = ''
      this.$refs.photoInput.click()
    },
    on_photo_picked(event){
      const file = event.target.files && event.target.files[0]
      if(file && this.photo_target) this.photo_target.photos.push(file)
      this.photo_target = null
    },
    payload(){
      return {
        order_id: this.context.order_id,
        source: this.context.source || (this.expected_total ? 'solicited' : 'unsolicited'),
        tracking_number: this.context.tracking_number || null,
        fulfillment_doc_id: this.context.fulfillment_doc_id || null,
        items: this.selected.map(r => ({
          fulfillment_line_item_id: r.fulfillment_line_item_id,
          name: r.name,
          quantity: r.receive_quantity,
          condition: r.condition,
          note: r.note,
          photos: r.photos,
        })),
      }
    },
    async submit(){
      this.saving = true
      this.error = ''
      try {
        this.result = await this.$store.dispatch('returns/register', this.payload())
        this.stage = 'put_away'
        this.put_away_index = 0
        this.notify_result(`Return registered on ${this.detail.order.name}`)
      } catch (e) {
        if(e.details?.code === 'return_create_failed') await this.offer_restock_without_return(e.message)
        else this.error = e.message
      } finally {
        this.saving = false
      }
    },
    // The put-away stage is confirmation enough; only a failed automatic step, or an exchange that
    // could not be released although the setting asked for it, needs a word
    notify_result(text){
      const results = this.result?.after_receipt?.results || []
      const failed = results.filter(r => r.action === 'failed')
      if(failed.length) return this.$store.commit('app/SET_SNACK_BAR', {
        text: `${text}, but Shopify could not ${this.result.after_receipt.mode === 'refund' ? 'refund' : 'close'} ${failed.map(r => r.return_name || 'the return').join(', ')}: ${failed[0].error}. It is left open under "Awaiting store owner".`,
        type: 'warning',
      })
      const paying = results.find(r => r.exchange === 'awaiting_payment')
      if(paying) return this.$store.commit('app/SET_SNACK_BAR', {
        text: paying.invoice_sent
          ? `${text}. The replacement costs ${paying.balance_due} ${paying.currency || ''} more; Shopify sent the customer a payment request and the exchange is released once paid.`
          : `${text}. The replacement costs ${paying.balance_due} ${paying.currency || ''} more but the payment request could not be sent: ${paying.error}. Send the invoice from the Shopify admin.`,
        type: paying.invoice_sent ? 'info' : 'warning',
      })
      const held = results.find(r => typeof r.exchange === 'string' && r.exchange.startsWith('left_for_owner'))
      if(held) return this.$store.commit('app/SET_SNACK_BAR', {
        text: `${text}. The exchange on ${held.return_name || 'the return'} was not released: ${held.exchange.replace(/^left_for_owner: /, '')}.`,
        type: 'warning',
      })
      return null
    },
    // Shopify refuses to open a return when the order was already refunded. Offer to restock anyway.
    async offer_restock_without_return(message){
      if(this.selected.some(r => r.condition === 'hold')){
        this.error = `${message} Items can not be held in this case: choose Good, Damaged or Missing and try again.`
        return
      }
      const { ok } = await this.$prompt({
        text: `${message}\n\nRestock the good items directly in Shopify inventory instead? The return is logged here, but no return is created in Shopify.`,
        buttons: [
          { text: 'Restock without return', value: { ok: true } },
          { text: 'Cancel', value: { ok: false } },
        ],
      })
      if(!ok) return
      this.saving = true
      try {
        this.result = await this.$store.dispatch('returns/register', { ...this.payload(), without_return: true })
        this.stage = 'put_away'
        this.put_away_index = 0
      } catch (e) {
        this.error = e.message
      } finally {
        this.saving = false
      }
    },
    // Each card confirms one item; confirming the last one closes the dialog so staff land back on the scanner
    put_away_done(){
      this.put_away_index++
      if(this.put_away_index >= this.put_away_items.length) this.finish()
    },
    finish(){
      this.$emit('done', this.result)
      this.$emit('input', false)
    },
    close(){
      if(this.stage === 'put_away') return this.finish()
      this.$emit('input', false)
    },
  },
}
</script>

<style lang="scss" scoped>
.dialog-header {
  padding: 16px 24px;
}

.dialog-content {
  padding: 20px 24px;
  background-color: #fafafa;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 240px;
}

.order-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

.item-row {
  background-color: white;
  padding: 12px 16px;
  transition: opacity 0.2s ease;

  &--inactive {
    opacity: 0.75;
  }
  &--disabled {
    opacity: 0.5;
  }
}

.group-label {
  display: flex;
  align-items: center;
  font-size: 0.8125rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
  margin: 4px 0 8px;

  &--other {
    cursor: pointer;
    font-weight: 500;
    margin-top: 12px;
    user-select: none;
  }
}

.item-row--other {
  background-color: #f5f5f5;
}

.fade-list-enter-active,
.fade-list-leave-active {
  transition: all 0.3s ease;
}
.fade-list-enter,
.fade-list-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

@media (max-width: 600px) {
  .dialog-content {
    padding: 16px;
  }
}
</style>
